/**
 * Manager for websockets
 */
import _ from 'underscore';
import Backbone from 'backbone';
import Radio from 'backbone.radio';

  var WS_STATUS_MAP = {
      0: "connecting",
      1: "open",
      2: "closing",
      3: "closed"
    },
    WS_STATUS = { 'connecting' : 0, 'open' : 1, 'closing' : 2, 'closed' : 3 };

  var WsManager = function(options) {
    // URL for the websocket
    this.url = options.url;

    // placeholder for the websocket
    this.ws = null;

    // Flag to tell us if we're trying to reconnect
    this.connecting = false;

    // Flag for connection error message so we can remove it later if we reconnect
    this.displaying_err_msg = false;

    // Four attempt intervals: 0.1s, 1s, 10s, 30s
    var attempt_intervals = [0.1, 1, 10, 30];

    this.connection_warn_threshold = attempt_intervals.length;

    this.get_attempt_interval = function() {
      if (this.connection_attempts < attempt_intervals.length) {
        return attempt_intervals[this.connection_attempts];
      } else {
        return attempt_intervals[attempt_intervals.length - 1];
      }
    };

    // Track the number of retries
    this.connection_attempts = 0;

    // Verbose websocket logging
    this.verbose = options.verbose || false;
  };
  _.extend(WsManager.prototype, Backbone.Events);

  /**
   * Reconnect the websocket manager if it's necessary.
   * Takes a WsManager instance as an argument
   */
  function attempt_reconnect(wsm) {
    if (wsm.connection_attempts === wsm.connection_warn_threshold) {
      wsm.displaying_err_msg = true;
      Radio.request('alerts', 'showAlert', {
        message: "Error re-connecting to WebSocket service. Please try refreshing your browser.",
        messageType: 'danger',
        timeout: 0
      });
    }

    // Increment the attempt counter and try to connect after an interval
    if (!wsm.connecting) {
      var delay_in_seconds = wsm.get_attempt_interval();
      wsm.verbose && console.log('scheduling another WebSocket connect attempt in ' + delay_in_seconds + ' seconds');
      setTimeout(_.bind(wsm.connect, wsm), delay_in_seconds * 1000);
      wsm.connection_attempts++;
    }
  }
  // OnError
  function onerror(event){
    this.verbose && console.log('websocket error', event);
    this.connecting = false;
    this.trigger('error', event, this.ws);
  }

  // OnClose
  function onclose(event){
    this.verbose && console.log('websocket close', event);
    this.connecting = false;
    this.trigger('close', event, this.ws);
    if (!event.wasClean) attempt_reconnect(this);
  }

  // On Open
  function onopen(event){
    this.verbose && console.log('websocket open', event);
    this.trigger('open', event, this.ws);
    this.connecting = false;
    this.connection_attempts = 0;

    // Show connection restored message if applicable
    if (this.displaying_err_msg) {
      Radio.request('alerts', 'showAlert', {
        message: "Streaming log connection restored",
        messageType: 'success'
      });
      this.displaying_err_msg = false;
    }
  }

  // On message
  function onmessage(event) {
    var json, command;
    this.trigger('message', event, this.ws);

    // trigger a command if we see one. this is an IDE-specific notion
    try {
      json = JSON.parse(event.data);
    } catch (e) {
      this.verbose && console.error(e);
      return;
    }
    if (json.command) {
      command = json.command;
      this.trigger('command', json);
      this.trigger('command:' + command, json);
    }
  }
  /**
   * Open a websocket connection and bind handler functions to the
   * resulting websockets instance
   */
  function connect(){
    // If we've already created the websocket, close it first
    if (this.ws && typeof this.ws.close === 'function') {
      this.ws.close(1000, 'Refreshing websocket');
      this.ws = null;
      delete this.ws;
    }
    this.verbose && console.log('WebSocket attempting to connect...');
    this.connecting = true;
    var ws = this.ws = new WebSocket(this.url);

    // Bind functions to the ws error handler
    ws.onerror   = _.bind(onerror,     this);
    ws.onclose   = _.bind(onclose,     this);
    ws.onopen    = _.bind(onopen,      this);
    ws.onmessage = _.bind(onmessage,   this);

    this.trigger('connect', ws);
  }
  /**
   * Send data via the websocket when the connection is available.
   * @param data string
   */
  function send(data) {
    var ws = this.ws;
    // If the websocket is open, send free and clear
    if (ws.readyState === WebSocket.OPEN) {
      return ws.send(data);
    }
    // if the websocket is connecting, wait until it connects then
    // attempt to send
    if (ws.readyState === WebSocket.CONNECTING) {
      this.verbose && console.warn("Tried to send but websocket was connecting.");
      return this.listenToOnce(this, 'open', _.bind(this.send, this, data));
    }
    // Otherwise the socket is either CLOSING or CLOSED, and we can't do anything
    this.verbose && console.error("Tried to send but websocket was %s",
                  WS_STATUS_MAP[ws.readyState]);
  }
  /**
   * Close the websocket connection
   * @param code   int
   * @param reason string (no longer than 123 characters)
   */
  function close() {
    this.ws.close.apply(this.ws, arguments);
  }

  /**
   * Is the websocket open?
   */
  function is_open(){
    return this.ws && this.ws.readyState === WS_STATUS['open'];
  }
  /**
   * Is the websocket connecting?
   */
  function is_connecting(){
    return this.ws && this.ws.readyState === WS_STATUS['connecting'];
  }
  /**
   * Is the websocket ?
   */
  function is_closing(){
    return this.ws && this.ws.readyState === WS_STATUS['closing'];
  }
  /**
   * Is the websocket closed?
   */
  function is_closed(){
    return this.ws && this.ws.readyState === WS_STATUS['closed'];
  }
  _.extend(WsManager.prototype, {
    connect       : connect,
    send          : send,
    close         : close,
    is_open       : is_open,
    is_connecting : is_connecting,
    is_closing    : is_closing,
    is_closed     : is_closed,
    WS_STATUS_MAP : WS_STATUS_MAP,
    WS_STATUS     : WS_STATUS
  });

  export default WsManager;

